// `?no-inline` forces Vite to emit each asset as a hashed file URL instead of
// inlining it as a `data:image/webp;base64,...` URI (every webp here is under
// the default 4 KB inline limit). Chakra's `parseGradient` mangles `url(...)`
// values containing a comma (which data URIs always have between `base64` and
// the payload) into `url-gradient(...)`, so inlined backgrounds render as
// `background-image: none`. `?url` is not enough — it still respects the inline
// limit; only `?no-inline` opts out entirely.
import previewBgAfternoon from "src/assets/cep-backgrounds/preview-bg-afternoon.webp?no-inline";
import previewBgMorning from "src/assets/cep-backgrounds/preview-bg-morning.webp?no-inline";
import previewBgNight from "src/assets/cep-backgrounds/preview-bg-night.webp?no-inline";

export type PreviewGradient = {
  from: string;
  via?: string;
  viaPercent?: number;
  to: string;
};

export type PreviewPalette = {
  name: "morning" | "afternoon" | "night";
  gradient: PreviewGradient;
  backgroundGradient?: string;
  imageUrl: string;
  textColor: string;
};

// Uniform 20% white wash painted over each gradient image on preview surfaces,
// matching Figma's `rgba(255, 255, 255, 0.2)` overlay. `getPreviewBackgroundCss`
// composes this on top of `imageUrl`.
const SURFACE_WHITE_WASH =
  "linear-gradient(rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2))";

export const PREVIEW_PALETTES: readonly [
  PreviewPalette,
  PreviewPalette,
  PreviewPalette,
] = [
  {
    name: "morning",
    gradient: {
      from: "#c5cf8a",
      via: "#d1e0d0",
      viaPercent: 60,
      to: "#d7e5eb",
    },
    imageUrl: previewBgMorning,
    backgroundGradient: SURFACE_WHITE_WASH,
    textColor: "#2d4a17",
  },
  {
    name: "afternoon",
    gradient: { from: "#ffd182", to: "#f5e9cf" },
    imageUrl: previewBgAfternoon,
    backgroundGradient: SURFACE_WHITE_WASH,
    textColor: "#5e4310",
  },
  {
    name: "night",
    gradient: { from: "#d8d4ec", to: "#f3e2e5" },
    imageUrl: previewBgNight,
    backgroundGradient: SURFACE_WHITE_WASH,
    textColor: "#432050",
  },
];

function hashSeed(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

export function getPreviewPalette(seed: string): PreviewPalette {
  const index = hashSeed(seed) % PREVIEW_PALETTES.length;
  return PREVIEW_PALETTES[index] ?? PREVIEW_PALETTES[0];
}

export function getPreviewGradient(seed: string): PreviewGradient {
  return getPreviewPalette(seed).gradient;
}

export function getPreviewBackgroundImage(seed: string): string {
  return getPreviewPalette(seed).imageUrl;
}

// Raw-CSS variant of `buildLinearGradient` (which emits Chakra's `linear(...)`
// shorthand — invalid inside a `background-image` layer list).
function buildCssLinearGradient(gradient: PreviewGradient): string {
  const { from, via, viaPercent, to } = gradient;
  return via !== undefined && viaPercent !== undefined
    ? `linear-gradient(180deg, ${from}, ${via} ${viaPercent}%, ${to})`
    : `linear-gradient(180deg, ${from}, ${to})`;
}

/**
 * Composed `background-image` CSS for a preview surface: the palette's optional
 * `backgroundGradient` overlaid on top of its `imageUrl`. Use this anywhere a
 * preview shows the palette so the surface white wash (`SURFACE_WHITE_WASH`)
 * applies consistently on top of the gradient image.
 *
 * The palette's own gradient sits underneath as the bottom layer: gradients
 * paint instantly while the webp is a network fetch, so without it the first
 * paint showed only the white wash — a briefly wrong color until the image
 * arrived (guaranteed on cold caches; the assets can't be inlined, see the
 * `?no-inline` note above).
 */
export function getPreviewBackgroundCss(seed: string): string {
  const palette = getPreviewPalette(seed);
  const layers = [
    palette.backgroundGradient,
    `url(${palette.imageUrl})`,
    buildCssLinearGradient(palette.gradient),
  ];
  return layers.filter(Boolean).join(", ");
}

/**
 * Resolves the seed used to derive a preview palette for a piece of content.
 * Prefers the campaign id so every surface tied to the same campaign renders
 * the same hue; falls back to a surface-natural id (message id, journey node id).
 *
 * If both inputs are falsy, returns "" — the empty seed deterministically maps
 * to palette index 0 via the existing hash, so callers without an id still get
 * a stable (if shared) palette rather than a runtime error.
 */
export function getPaletteSeed(args: {
  campaignId?: string | number | null;
  fallbackId?: string | number | null;
}): string {
  if (
    args.campaignId !== undefined &&
    args.campaignId !== null &&
    args.campaignId !== ""
  ) {
    return String(args.campaignId);
  }
  if (
    args.fallbackId !== undefined &&
    args.fallbackId !== null &&
    args.fallbackId !== ""
  ) {
    return String(args.fallbackId);
  }
  return "";
}

function hexToRgba(hex: string, opacity: number): string {
  const cleaned = hex.replace("#", "");
  const r = Number.parseInt(cleaned.slice(0, 2), 16);
  const g = Number.parseInt(cleaned.slice(2, 4), 16);
  const b = Number.parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function applyOpacity(stop: string, opacity: number): string {
  return opacity >= 1 ? stop : hexToRgba(stop, opacity);
}

/**
 * Builds the Chakra `bgGradient` shorthand for a preview gradient. When `via`
 * is set, renders a 3-stop gradient at the supplied percent. `opacity < 1`
 * converts each stop to `rgba(...)` so the stops fade together (used by the
 * profile-inspector card per Figma's 70%-alpha annotation).
 */
export function buildLinearGradient(
  gradient: PreviewGradient,
  opacity = 1,
): string {
  const from = applyOpacity(gradient.from, opacity);
  const to = applyOpacity(gradient.to, opacity);
  if (gradient.via !== undefined && gradient.viaPercent !== undefined) {
    const via = applyOpacity(gradient.via, opacity);
    return `linear(to-b, ${from}, ${via} ${gradient.viaPercent}%, ${to})`;
  }
  return `linear(to-b, ${from}, ${to})`;
}
